import { useCallback, useEffect, useState } from 'react';
import { useMaFiche } from '../api/maFiche';
import { authApi, invitationsApi } from '../api/resources';
import { LienFormules } from './LienFormules.jsx';
import {
  MESSAGE_FICHE_NON_ENREGISTREE,
  ROLES,
  dateHeureSuisse,
  estAccesParPin,
  ficheEnregistree,
  libelleInvitation,
} from '../lib/acces';
import { liensEnvoi } from '../lib/envoi';
import { tr, trf } from '../lib/i18n';
import { useModeConnexion } from '../api/modeConnexion';
import { C } from '../theme/palette';
import { ACCESS_ROLE } from '../lib/access';
import { NOM_LOGICIEL } from '../lib/marque';

const panneau = {
  marginTop: 8,
  // Dans la rangée des boutons de la fiche, le panneau passe dessous.
  flexBasis: '100%',
  order: 1,
  border: '1px solid ' + C.border,
  borderRadius: 10,
  padding: '11px 13px',
};

const petitBouton = (couleur, plein) => ({
  background: plein ? couleur : C.white,
  border: '1px solid ' + couleur,
  color: plein ? '#fff' : couleur,
  borderRadius: 8,
  padding: '6px 10px',
  fontSize: 9.5,
  fontWeight: 800,
  cursor: 'pointer',
});

/**
 * Invite un employé à ouvrir son accès, par un lien.
 *
 * Deux chemins coexistaient : le patron dictait un code PIN et le code de la
 * société, ou créait un compte à mot de passe provisoire qu'il recopiait dans
 * un message. Le bouton « Inviter » les unifie : le serveur tire un lien, que
 * le patron partage depuis son téléphone — e-mail, WhatsApp, SMS — ou que le
 * serveur envoie lui-même quand il sait le faire. L'employé qui le suit choisit
 * son code et entre.
 *
 * La fiche suit l'invitation — envoyée, acceptée, expirée — et garde la main
 * sur l'accès ouvert : changer de rôle, le fermer.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.employe fiche de l'employé
 * @param {object} [props.soc] entreprise
 * @param {(route: string) => void} [props.onNav] navigation, vers l'abonnement
 *        quand la formule n'autorise pas un compte de plus
 * @returns {import('react').ReactElement} le bouton et son suivi
 */
export function InvitationEmploye({ employe, soc, onNav }) {
  // L'invité, ouvrier compris, choisit son adresse et un mot de passe ; le
  // code à 4 chiffres ne sert plus qu'à ceux qui l'ont déjà, tant qu'il vaut.
  const codeOuvrier = useModeConnexion()?.codeOuvrier === true;
  const [choixDuRole, setChoixDuRole] = useState(false);
  const [role, setRole] = useState('OUVRIER');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [refus, setRefus] = useState(null);
  const [invitation, setInvitation] = useState(null);
  // Le lien et le message ne sortent du serveur qu'a la creation : ils sont
  // gardes ici tant que l'ecran reste ouvert, puis oublies.
  const [partage, setPartage] = useState(null);
  const [copie, setCopie] = useState(false);
  // Pourquoi l'e-mail d'invitation n'est pas parti (pas d'adresse, messagerie
  // non raccordée, envoi refusé) : le patron croyait le lien parti.
  const [courrielErreur, setCourrielErreur] = useState(null);
  const [test, setTest] = useState(false);
  const [existant, setExistant] = useState(null);
  // Vrai quand la fiche a un compte, mais plus aucun d'ouvert : l'accès a été
  // fermé. Sans ce mot, la fiche disait encore « invitation acceptée » comme
  // si l'employé entrait toujours.
  const [accesFerme, setAccesFerme] = useState(false);

  const enregistree = ficheEnregistree(employe);
  const employeId = enregistree ? employe.id : null;
  // La fiche du patron lui-meme : rien a inviter, son compte existe. Elle se
  // rattache a lui d'un geste, et c'est tout ce que la fiche en dit.
  const maFiche = useMaFiche();
  const laMienne = maFiche.estLaMienne(employe);

  const recharge = useCallback(() => {
    if (employeId == null) return;
    if (typeof authApi.comptes === 'function') {
      authApi
        .comptes()
        .then((comptes) => {
          const lies = (comptes || []).filter((c) => c.employeeId === employeId);
          const trouve = lies.find((c) => c.actif !== false);
          setExistant(trouve || null);
          setAccesFerme(!trouve && lies.length > 0);
        })
        .catch(() => {});
    }
    if (typeof invitationsApi.etat === 'function') {
      invitationsApi
        .etat(employeId)
        .then((etat) => setInvitation(etat || null))
        .catch(() => {});
    }
  }, [employeId]);

  useEffect(() => {
    recharge();
  }, [recharge]);

  const invite = async (nouveauRole) => {
    setErreur('');
    setEnvoi(true);
    try {
      const creee = await invitationsApi.invite(employeId, nouveauRole || role);
      setInvitation(creee);
      setCourrielErreur(creee?.courrielErreur || null);
      setPartage(creee?.lien ? { lien: creee.lien, message: creee.message || creee.lien } : null);
      setCopie(false);
      setChoixDuRole(false);
    } catch (e) {
      setErreur(e?.message || 'L’invitation n’a pas pu être envoyée.');
      setRefus(e);
    } finally {
      setEnvoi(false);
    }
  };

  const annule = async () => {
    setErreur('');
    setEnvoi(true);
    try {
      await invitationsApi.annule(employeId);
      setPartage(null);
      recharge();
    } catch (e) {
      setErreur(e?.message || 'L’invitation n’a pas pu être annulée.');
    } finally {
      setEnvoi(false);
    }
  };

  const changeLeRole = async (nouveau) => {
    if (!existant || nouveau === existant.role) return;
    setErreur('');
    setEnvoi(true);
    try {
      setExistant(await authApi.modifieCompte(existant.id, nouveau));
    } catch (e) {
      setErreur(e?.message || 'Le rôle n’a pas pu être changé.');
      setRefus(e);
    } finally {
      setEnvoi(false);
    }
  };

  const copieLeLien = async () => {
    if (!partage?.lien) return;
    try {
      await navigator.clipboard.writeText(partage.lien);
      setCopie(true);
    } catch {
      setCopie(false);
    }
  };

  const suivi = libelleInvitation(invitation);
  const parPin = estAccesParPin(existant);
  // L'e-mail part du serveur, par la boîte de l'entreprise : le lien
  // « mailto » ouvrait la messagerie du téléphone (Gmail…) et l'invitation
  // partait d'une adresse privée. Restent WhatsApp et SMS.
  const liens = partage
    ? liensEnvoi(
        { email: employe?.email, tel: employe?.tel },
        { titre: 'Invitation — ' + (soc?.nom || NOM_LOGICIEL), message: partage.message },
      ).filter((l) => l.cle !== 'email')
    : [];

  const alerte = erreur && (
    <div role="alert" style={{ fontSize: 9.5, color: C.red, marginTop: 6, lineHeight: 1.5 }}>
      {erreur}
      <LienFormules erreur={refus} onNav={onNav} />
    </div>
  );

  if (laMienne) {
    return (
      <div style={panneau} data-testid="ma-fiche">
        <div style={{ fontSize: 9.5, fontWeight: 800 }}>
          {maFiche.rattachee
            ? '🔗 Ta fiche, rattachée à ton compte patron'
            : '👤 C’est ta fiche : elle porte l’adresse de ton compte'}
        </div>
        <div style={{ fontSize: 8.5, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>
          {maFiche.rattachee
            ? 'Ta paie sort comme celle de chacun, et « Mon espace terrain » s’ouvre sur elle.'
            : 'Pas d’invitation à envoyer : rattache-la à ton compte, et ton espace d’employé s’ouvre dessus.'}
        </div>
        <div style={{ marginTop: 7 }}>
          {maFiche.rattachee ? (
            <button onClick={() => onNav && onNav('ouvrier')} style={petitBouton(C.teal, true)}>
              👷 Mon espace terrain
            </button>
          ) : (
            <button
              disabled={maFiche.enCours || !enregistree}
              onClick={() => maFiche.seRattache()}
              style={petitBouton(C.navy, true)}
            >
              {maFiche.enCours ? '⏳ Rattachement…' : '🔗 Rattacher à mon compte'}
            </button>
          )}
        </div>
        {maFiche.erreur && (
          <div role="alert" style={{ fontSize: 9.5, color: C.red, marginTop: 6 }}>
            {maFiche.erreur}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {/*
        La fiche tout juste creee n'a pas encore d'identifiant du serveur : une
        invitation creee a ce moment-la naissait sans fiche. Le bouton attend,
        et le dit.
      */}
      <button
        onClick={() => enregistree && setChoixDuRole((o) => !o)}
        disabled={!enregistree || envoi}
        title={enregistree ? undefined : MESSAGE_FICHE_NON_ENREGISTREE}
        style={{
          background: C.green,
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          padding: '7px 12px',
          fontSize: 9.5,
          fontWeight: 700,
          cursor: enregistree ? 'pointer' : 'wait',
          opacity: enregistree ? 1 : 0.55,
        }}
      >
        {suivi?.enAttente ? '🔁 Renvoyer l’invitation' : '✉️ Inviter'}
      </button>
      {!enregistree && (
        <div
          role="status"
          style={{ flexBasis: '100%', order: 1, fontSize: 9, color: C.muted, marginTop: 6 }}
        >
          {MESSAGE_FICHE_NON_ENREGISTREE}
        </div>
      )}

      {choixDuRole && (
        <div style={panneau}>
          <div style={{ fontSize: 9.5, fontWeight: 800, marginBottom: 7 }}>
            Quel accès pour {employe?.prenom || employe?.nom} ?
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 9 }}>
            {/* Le technicien et le directeur des travaux invitent un ouvrier ;
                un compte du bureau, seul le patron l'ouvre (le serveur aussi). */}
            {ROLES.filter((r) => ACCESS_ROLE === 'patron' || r.cle === 'OUVRIER').map((r) => (
              <div
                key={r.cle}
                onClick={() => setRole(r.cle)}
                style={{
                  border: '1px solid ' + (role === r.cle ? C.navy : C.border),
                  background: role === r.cle ? C.navyXL : C.white,
                  borderRadius: 8,
                  padding: '7px 10px',
                  cursor: 'pointer',
                }}
              >
                <div style={{ fontSize: 10, fontWeight: 800, color: C.ink }}>{r.nom}</div>
                <div style={{ fontSize: 8.5, color: C.muted }}>{r.quoi}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 8.5, color: C.muted, marginBottom: 8, lineHeight: 1.5 }}>
            L’employé reçoit un lien valable 24 heures. Il choisit son adresse e-mail et un mot de
            passe, avec lesquels il se connectera.
          </div>
          {alerte}
          <div style={{ display: 'flex', gap: 6, marginTop: alerte ? 7 : 0 }}>
            <button
              onClick={() => {
                setChoixDuRole(false);
                setErreur('');
              }}
              style={{
                flex: 1,
                background: C.navyXL,
                border: 'none',
                color: C.grey,
                borderRadius: 8,
                padding: 10,
                fontSize: 10,
                fontWeight: 800,
                cursor: 'pointer',
              }}
            >
              Annuler
            </button>
            <button
              onClick={() => invite()}
              disabled={envoi}
              style={{
                flex: 2,
                background: C.green,
                border: 'none',
                color: '#fff',
                borderRadius: 8,
                padding: 10,
                fontSize: 10,
                fontWeight: 800,
                cursor: envoi ? 'wait' : 'pointer',
                opacity: envoi ? 0.6 : 1,
              }}
            >
              {envoi
                ? 'Envoi…'
                : suivi?.enAttente
                  ? '🔁 Renvoyer le lien'
                  : '✉️ Envoyer l’invitation'}
            </button>
          </div>
        </div>
      )}

      {suivi && (
        <div
          style={{
            ...panneau,
            background:
              suivi.etat === 'ACCEPTEE'
                ? C.greenL
                : suivi.etat === 'EN_ATTENTE'
                  ? C.amberL
                  : C.white,
          }}
        >
          <div style={{ fontSize: 9.5, fontWeight: 800 }} data-testid="suivi-invitation">
            {suivi.etat === 'ACCEPTEE' ? '✓ ' : suivi.etat === 'EN_ATTENTE' ? '⏳ ' : ''}
            {suivi.libelle}
          </div>
          {accesFerme && (
            <div style={{ fontSize: 8.5, color: C.red, fontWeight: 700, marginTop: 3 }}>
              🔒 Accès fermé — une nouvelle invitation le rouvre.
            </div>
          )}
          {invitation?.courrielEnvoyeLe && (
            <div style={{ fontSize: 8.5, color: C.green, fontWeight: 700, marginTop: 3 }}>
              📧{' '}
              {trf(
                'Envoyée par e-mail à {0} le {1}.',
                employe?.email || '',
                dateHeureSuisse(invitation.courrielEnvoyeLe),
              )}
            </div>
          )}
          {courrielErreur && !invitation?.courrielEnvoyeLe && (
            <div
              role="alert"
              style={{
                fontSize: 8.5,
                color: C.red,
                fontWeight: 700,
                marginTop: 3,
                lineHeight: 1.5,
              }}
            >
              ⚠️{' '}
              {courrielErreur === 'SANS_ADRESSE'
                ? tr(
                    'Pas d’adresse e-mail sur sa fiche : ajoute-la dans « Modifier l’employé », puis touche « ✉️ E-mail ».',
                  )
                : courrielErreur === 'NON_RACCORDE'
                  ? tr(
                      'L’envoi par e-mail n’est pas raccordé : partage le lien par WhatsApp, SMS ou en le copiant.',
                    )
                  : trf(
                      'L’e-mail à {0} n’est pas parti : vérifie l’adresse, ou partage le lien par WhatsApp, SMS ou en le copiant.',
                      employe?.email || '',
                    )}
            </div>
          )}
          {partage && suivi.enAttente && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 8.5, color: C.grey, marginBottom: 5, lineHeight: 1.5 }}>
                Partagez le lien à {employe?.prenom || employe?.nom} : il choisit son adresse e-mail
                et un mot de passe, et entre.
              </div>
              <div
                data-testid="lien-invitation"
                style={{
                  fontFamily: 'monospace',
                  fontSize: 8.5,
                  background: C.white,
                  border: '1px solid ' + C.border,
                  borderRadius: 6,
                  padding: '5px 7px',
                  overflowWrap: 'anywhere',
                  marginBottom: 6,
                }}
              >
                {partage.lien}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {liens.map((l) => (
                  <a
                    key={l.cle}
                    href={l.href}
                    {...(l.externe ? { target: '_blank', rel: 'noreferrer' } : {})}
                    style={{ textDecoration: 'none', flex: 1, minWidth: 90 }}
                  >
                    <div
                      style={{
                        background: C.navy,
                        color: '#fff',
                        borderRadius: 8,
                        padding: 8,
                        textAlign: 'center',
                        fontSize: 10,
                        fontWeight: 800,
                      }}
                    >
                      {l.icone} {l.nom}
                    </div>
                  </a>
                ))}
                {/* Par e-mail, à l'adresse de sa fiche, depuis la boîte de
                    l'entreprise (ou de SwissTech) : un nouveau lien part, et
                    l'écran dit s'il est parti. */}
                <button
                  type="button"
                  disabled={envoi}
                  onClick={() => invite(invitation?.role)}
                  style={{
                    flex: 1,
                    minWidth: 90,
                    background: C.navy,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: 8,
                    fontSize: 10,
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  ✉️ {tr('E-mail')}
                </button>
                <button onClick={copieLeLien} style={{ ...petitBouton(C.navy, false), flex: 1 }}>
                  {copie ? '✓ Copié' : '📋 Copier le lien'}
                </button>
              </div>
              {/* Le patron essaie l'espace de l'employé lui-même, avant de
                  l'inviter pour de bon : le même lien, ouvert à part, pour ne
                  pas fermer sa propre session. */}
              <button
                onClick={() => {
                  copieLeLien();
                  setTest(true);
                }}
                style={{ ...petitBouton(C.teal, false), width: '100%', marginTop: 6 }}
              >
                🧪 Tester moi-même l’espace employé
              </button>
              {test && (
                <div
                  role="note"
                  style={{
                    marginTop: 6,
                    background: C.tealL,
                    border: '1px solid ' + C.teal,
                    borderRadius: 8,
                    padding: '8px 10px',
                    fontSize: 10.5,
                    color: C.ink,
                    lineHeight: 1.55,
                  }}
                >
                  <div style={{ fontWeight: 800 }}>
                    {trf(
                      'Le lien est copié. Pour voir SwissTech comme {0} :',
                      employe?.prenom || employe?.nom || '',
                    )}
                  </div>
                  <div>
                    1. Ouvrez une fenêtre privée (Safari : onglets → Privé) ou un autre téléphone.
                  </div>
                  <div>2. Collez le lien, choisissez une adresse e-mail et un mot de passe.</div>
                  <div>3. Vous êtes dans son espace : chantiers, heures, photos, messages.</div>
                  <div style={{ color: C.muted, marginTop: 3 }}>
                    Dans cette fenêtre-ci, vous restez connecté en patron.
                  </div>
                </div>
              )}
            </div>
          )}
          {suivi.enAttente && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button
                disabled={envoi}
                onClick={() => invite(invitation?.role || role)}
                style={petitBouton(C.navy, false)}
              >
                🔁 Renvoyer
              </button>
              <button disabled={envoi} onClick={annule} style={petitBouton(C.red, false)}>
                ✕ Annuler l’invitation
              </button>
            </div>
          )}
          {!choixDuRole && alerte}
        </div>
      )}

      {existant && !parPin && (
        <div style={panneau}>
          <div style={{ fontSize: 9.5, fontWeight: 800, marginBottom: 4 }}>
            🔑 Accès ouvert · {existant.email}
          </div>
          <div style={{ fontSize: 8.5, color: C.muted, marginBottom: 7 }}>
            Changer de rôle déconnecte la personne : elle reprend avec le nouveau.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 7 }}>
            {ROLES.filter((r) => ACCESS_ROLE === 'patron' || r.cle === existant.role).map((r) => (
              <button
                key={r.cle}
                disabled={envoi}
                onClick={() => changeLeRole(r.cle)}
                style={{
                  border: '1px solid ' + (existant.role === r.cle ? C.navy : C.border),
                  background: existant.role === r.cle ? C.navyXL : C.white,
                  color: C.ink,
                  borderRadius: 8,
                  padding: '6px 9px',
                  fontSize: 9.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                }}
              >
                {r.nom}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 8.5, color: C.muted, lineHeight: 1.5 }}>
            Pour fermer cet accès : Réglages → Mon compte → Comptes de l’équipe.
          </div>
        </div>
      )}

      {existant && parPin && (
        <div style={panneau}>
          <div style={{ fontSize: 9.5, fontWeight: 800 }}>
            {codeOuvrier ? '🔢 Accès par code PIN' : '🔢 Ancien accès par code PIN'}
          </div>
          <div style={{ fontSize: 8.5, color: C.muted, margin: '3px 0 6px', lineHeight: 1.5 }}>
            {codeOuvrier
              ? 'L’employé entre sur chantier avec son code à 4 chiffres. Envoyez-lui une nouvelle invitation : il choisira son adresse e-mail et un mot de passe.'
              : 'Le code à 4 chiffres n’ouvre plus SwissTech. Envoyez-lui une nouvelle invitation : il choisira son adresse e-mail et un mot de passe.'}
          </div>
          <div style={{ fontSize: 8.5, color: C.muted, lineHeight: 1.5 }}>
            Pour fermer cet accès : Réglages → Mon compte → Comptes de l’équipe.
          </div>
        </div>
      )}

      {!suivi && !choixDuRole && alerte}
    </>
  );
}
